import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTRPC } from '../lib/trpc'
import { Field, FieldSection, Select } from './index'

/**
 * Which model the assistant runs on, chosen in the composer.
 *
 * Closed by default and closed for almost everybody: the answer is `auto`, and
 * a control nobody needs does not belong in front of the box they type into.
 * Whoever wants to try a different model opens the composer's options and finds
 * it there, beside the attachment control, where a chat interface puts it.
 *
 * The choice is the person's own — it changes which model answers THEIR turns —
 * so there is no permission on it and nothing to explain to the rest of the shul.
 *
 * Revealed as a row rather than a popover, for the reason every other revealed
 * option row exists: these are selects, and a select inside a floating panel
 * opens its menu into a surface with no room to read it.
 */
/** The label an option carries, for a value held on the person. */
function labelOf(
  groups: { options: { value: string; label: string }[] }[],
  value: string,
): string {
  return (
    groups.flatMap((g) => g.options).find((o) => o.value === value)?.label ??
    value
  )
}

/**
 * What is pinned, said in the composer once the options are shut.
 *
 * A choice that only exists behind a control you have to open is a choice you
 * forget you made — and this one changes what every answer costs and how good
 * it is. Plain `auto` says nothing, because that is the state the panel is in
 * for almost everybody and naming it would be chrome about nothing.
 *
 * Text, not a control: the options toggle beside it already opens the place
 * this is changed, and a region gets one entry point.
 */
export function AssistantModelPin() {
  const trpc = useTRPC()
  const { data } = useQuery(trpc.agent.models.queryOptions())
  if (!data) return null

  const pinned = [
    data.conversation.current !== 'auto'
      ? labelOf(data.conversation.groups, data.conversation.current)
      : null,
    data.attachments.current !== 'auto'
      ? labelOf(data.attachments.groups, data.attachments.current)
      : null,
  ].filter(Boolean)

  if (!pinned.length) return null
  return <span className="asst__pin">{pinned.join(' · ')}</span>
}

export function AssistantModels() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const choices = useQuery(trpc.agent.models.queryOptions())
  const set = useMutation(
    trpc.agent.setModels.mutationOptions({
      onSuccess: () =>
        qc.invalidateQueries({ queryKey: trpc.agent.models.queryKey() }),
    }),
  )

  const data = choices.data
  if (!data) return null

  return (
    <div className="asst__models">
      {/* Named once, above both: a label reading "Conversation" over a control
          saying "Automatic" never says what is being chosen. */}
      <FieldSection title="Model">
        <Field
          label="Conversation"
          info="Which model answers. Automatic picks one for each turn; the rest are every model that can use this assistant's tools, priced per million tokens in and out."
        >
          <Select
            value={data.conversation.current}
            options={data.conversation.groups}
            searchable
            side="backward"
            maxHeight="50dvh"
            disabled={set.isPending}
            onValueChange={(conversation) => set.mutate({ conversation })}
          />
        </Field>
        <Field
          label="Attachments"
          info="Which model reads what you attach. Only models that can read it are listed, and it applies to that reading alone — the conversation keeps the model above."
        >
          <Select
            value={data.attachments.current}
            options={data.attachments.groups}
            searchable
            side="backward"
            maxHeight="50dvh"
            disabled={set.isPending}
            onValueChange={(attachments) => set.mutate({ attachments })}
          />
        </Field>
      </FieldSection>
    </div>
  )
}
